/**
 * Sound Service - Manages celebration sound effects for XP/Gems earning
 */

class SoundService {
  constructor() {
    this.audioContext = null;
    this.soundEnabled = true;
    this.audioCache = null;
  }

  /**
   * Initialize audio context (required for Web Audio API)
   */
  initAudioContext() {
    if (typeof window === 'undefined') return;
    
    try {
      // Create AudioContext if not exists
      if (!this.audioContext) {
        const AudioContext = window.AudioContext || window.webkitAudioContext;
        this.audioContext = new AudioContext();
      }
      
      // Resume context if suspended (browser autoplay policy)
      if (this.audioContext.state === 'suspended') {
        this.audioContext.resume();
      }
    } catch (error) {
      console.warn('AudioContext initialization failed:', error);
    }
  }

  /**
   * Set sound enabled state
   */
  setSoundEnabled(enabled) {
    this.soundEnabled = enabled;
  }

  /**
   * Check if sound is enabled
   */
  isSoundEnabled() {
    return this.soundEnabled;
  }

  /**
   * Play celebration sound effect
   * Uses Web Audio API to generate a pleasant chime sound
   */
  playCelebrationSound() {
    if (!this.soundEnabled) return;
    if (typeof window === 'undefined') return;

    try {
      this.initAudioContext();
      
      if (!this.audioContext) {
        // Fallback: Try using HTML5 Audio
        this.playFallbackSound();
        return;
      }

      const now = this.audioContext.currentTime;
      
      // Create a pleasant ascending chime using a major pentatonic scale
      // Frequencies: C5, D5, E5, G5, A5 (pleasant, harmonious)
      const chimeNotes = [
        { freq: 523.25, delay: 0.0, volume: 0.35 },    // C5
        { freq: 587.33, delay: 0.06, volume: 0.3 },   // D5
        { freq: 659.25, delay: 0.12, volume: 0.25 },   // E5
        { freq: 783.99, delay: 0.18, volume: 0.2 },    // G5
        { freq: 880.00, delay: 0.24, volume: 0.15 }   // A5
      ];
      
      // Create each chime note with bell-like characteristics
      chimeNotes.forEach((note) => {
        const startTime = now + note.delay;
        const duration = 0.8; // Longer decay for bell-like sound
        
        // Create main oscillator with triangle wave for softer sound
        const osc = this.audioContext.createOscillator();
        const gain = this.audioContext.createGain();
        
        osc.type = 'triangle';
        osc.frequency.setValueAtTime(note.freq, startTime);
        
        // Add a subtle octave harmonic for richness
        const harmonicOsc = this.audioContext.createOscillator();
        const harmonicGain = this.audioContext.createGain();
        
        harmonicOsc.type = 'triangle';
        harmonicOsc.frequency.setValueAtTime(note.freq * 2, startTime); // Octave
        
        // Bell-like envelope: quick attack, long exponential decay
        // Main note
        gain.gain.setValueAtTime(0, startTime);
        gain.gain.linearRampToValueAtTime(note.volume, startTime + 0.015); // Quick attack
        gain.gain.exponentialRampToValueAtTime(0.001, startTime + duration); // Long decay
        
        // Harmonic (softer)
        harmonicGain.gain.setValueAtTime(0, startTime);
        harmonicGain.gain.linearRampToValueAtTime(note.volume * 0.3, startTime + 0.015);
        harmonicGain.gain.exponentialRampToValueAtTime(0.001, startTime + duration);
        
        osc.connect(gain);
        harmonicOsc.connect(harmonicGain);
        gain.connect(this.audioContext.destination);
        harmonicGain.connect(this.audioContext.destination);
        
        osc.start(startTime);
        harmonicOsc.start(startTime);
        osc.stop(startTime + duration);
        harmonicOsc.stop(startTime + duration);
      });
    } catch (error) {
      console.warn('Failed to play celebration sound:', error);
      // Fallback to HTML5 Audio
      this.playFallbackSound();
    }
  }

  /**
   * Fallback: Play sound using HTML5 Audio
   */
  playFallbackSound() {
    try {
      // Try to load and play a sound file if available
      const audio = new Audio('/sounds/celebration.mp3');
      audio.volume = 0.5;
      audio.play().catch(err => {
        console.warn('Could not play sound file:', err);
        // If file doesn't exist, generate a simple beep
        this.playSimpleBeep();
      });
    } catch (error) {
      console.warn('Fallback sound failed:', error);
      this.playSimpleBeep();
    }
  }

  /**
   * Play a simple beep as last resort
   */
  playSimpleBeep() {
    try {
      const audio = new Audio();
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.2);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (error) {
      console.warn('Simple beep failed:', error);
    }
  }
}

// Export singleton instance
export const soundService = new SoundService();

